import { memo, type SyntheticEvent } from "react";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import type { ResourceResponse, Option, BooleanSwitch } from "types";
import { useTypedSelector, useForm } from "hooks";
import { compareIds } from "util/support";
import { getFormInputProps, isFormChanged } from "util/forms";
import { convertToOptions } from "util/transformers";
import { selectMerchantOptions } from "features/merchants/selectors";
import { selectConstants } from "features/general/selectors";
import { useWaMessagesSetupsActions } from "features/waMessaging/actions";
import type { WaMessageSetup } from "features/waMessaging/types";
import Autocomplete from "ui/atoms/Autocomplete";
import ActionPanel from "ui/molecules/ActionPanel";
import { useWaMessageSetupContext } from "./WaMessageSetupProvider";

type FormValues = {
    readonly MOR: Option | null;
    readonly merchantId: Option | null;
    readonly transactionStatuses: Array<Option>;
    readonly phoneFrequency: Option | null;
    readonly isEnabled: boolean;
};

const validationSchema = Yup.object({
    MOR: Yup.object().nullable().required('Domain is required'),
    merchantId: Yup.object().nullable().required('Merchant is required'),
    transactionStatuses: Yup.array().min(1, 'At least one status is required'),
    phoneFrequency: Yup.object().nullable().required('Phone frequency is required')
});

const normalizeStatuses = (
    value: WaMessageSetup['transactionStatuses']
): Array<string> => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value.split(',').map(v => v.trim()).filter(Boolean);
};

const getInitialValues = (
    resource: WaMessageSetup | undefined,
    domains: Array<Option>,
    merchants: Array<Option>,
    frequencyOptions: Array<Option>,
    transactionStatusesOptions: Array<Option>
): FormValues => {
    if (!resource) {
        return {
            MOR: null,
            merchantId: null,
            transactionStatuses: [],
            phoneFrequency: null,
            isEnabled: false
        };
    }

    const statuses = normalizeStatuses(resource.transactionStatuses);

    return {
        MOR: domains.find(({ id }) => id === resource.MOR) ?? null,
        merchantId: merchants.find(({ id }) => compareIds(id, resource.merchantId!)) ?? null,
        transactionStatuses: transactionStatusesOptions.filter(({ id }) =>
            statuses.includes(String(id))
        ),
        phoneFrequency: frequencyOptions.find(({ id }) => id === resource.phoneFrequency) ?? null,
        isEnabled: Boolean(resource.isEnabled)
    };
};

type Props = {
    readonly resource: WaMessageSetup | undefined;
};

const WaMessageSetupForm = ({ resource }: Props) => {
    const merchants = useTypedSelector(selectMerchantOptions);
    const {
        ConsumerActiveDomain,
        WaPhoneFrequencies,
        TransactionStatus
    } = useTypedSelector(selectConstants);
    const domains = convertToOptions(ConsumerActiveDomain ?? []);
    const frequencyOptions = convertToOptions(WaPhoneFrequencies ?? []);
    const transactionStatusesOptions = convertToOptions(TransactionStatus ?? []);

    const { onClose } = useWaMessageSetupContext();
    const {
        createWaMessagesSetup,
        updateWaMessagesSetup
    } = useWaMessagesSetupsActions();

    const requestHandler = resource
        ? updateWaMessagesSetup
        : createWaMessagesSetup;

    const form = useForm<FormValues>({
        initialValues: getInitialValues(
            resource,
            domains,
            merchants,
            frequencyOptions,
            transactionStatusesOptions
        ),
        validationSchema,
        enableReinitialize: false,
        onSaveOrCreate: async (values) => {
            const body: Partial<WaMessageSetup> = {
                ...(resource ? { coreId: resource.coreId } : {}),
                MOR: values.MOR!.id as string,
                merchantId: values.merchantId!.id,
                transactionStatuses: (values.transactionStatuses ?? [])
                    .map((option: Option) => String(option.id))
                    .join(','),
                phoneFrequency: values.phoneFrequency!.id as string,
                isEnabled: (values.isEnabled ? 1 : 0) as BooleanSwitch
            };

            const [request, result] = requestHandler(body);
            const response = await request as ResourceResponse<WaMessageSetup>;

            if (response?.success) {
                onClose();
            }

            return result;
        }
    });

    const onAutocompleteChange = (fieldName: keyof FormValues) =>
        (_: SyntheticEvent, value: Option | Array<Option> | null) => {
            form.setFieldValue(fieldName, value, true);
            setTimeout(() => form.setFieldTouched(fieldName, true));
        };

    return (
        <Box
            component='form'
            onSubmit={form.handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    alignItems: 'flex-start',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)'
                    },
                    gap: { xs: 2, sm: 3 }
                }}
            >
                <Autocomplete
                    fullWidth
                    size='small'
                    layout={null}
                    options={domains}
                    disabled={Boolean(resource)}
                    getOptionLabel={(option: Option) => option.name}
                    isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                    value={form.values.MOR}
                    onChange={onAutocompleteChange('MOR')}
                    TextFieldProps={{
                        ...getFormInputProps('MOR', form),
                        required: true,
                        label: 'Domain'
                    }}
                />
                <Autocomplete
                    fullWidth
                    size='small'
                    layout={null}
                    options={merchants}
                    disabled={Boolean(resource)}
                    getOptionLabel={(option: Option) => option.name}
                    isOptionEqualToValue={(a: Option, b: Option) => compareIds(a.id, b.id)}
                    value={form.values.merchantId}
                    onChange={onAutocompleteChange('merchantId')}
                    TextFieldProps={{
                        ...getFormInputProps('merchantId', form),
                        required: true,
                        label: 'Merchant'
                    }}
                />
                <Autocomplete
                    fullWidth
                    multiple
                    size='small'
                    layout={null}
                    options={transactionStatusesOptions}
                    getOptionLabel={(option: Option) => option.name}
                    isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                    value={form.values.transactionStatuses}
                    onChange={onAutocompleteChange('transactionStatuses')}
                    TextFieldProps={{
                        ...getFormInputProps('transactionStatuses', form),
                        required: form.values.transactionStatuses.length === 0,
                        label: 'Transaction Statuses'
                    }}
                />
                <Autocomplete
                    fullWidth
                    size='small'
                    layout={null}
                    options={frequencyOptions}
                    getOptionLabel={(option: Option) => option.name}
                    isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                    value={form.values.phoneFrequency}
                    onChange={onAutocompleteChange('phoneFrequency')}
                    TextFieldProps={{
                        ...getFormInputProps('phoneFrequency', form),
                        required: true,
                        label: 'Phone Frequency'
                    }}
                />
                <FormControlLabel
                    label='Enabled'
                    control={
                        <Switch
                            checked={form.values.isEnabled}
                            onChange={(_, checked) => {
                                form.setFieldValue('isEnabled', checked, true);
                                setTimeout(() => form.setFieldTouched('isEnabled', true));
                            }}
                        />
                    }
                />
            </Box>
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || (Boolean(resource) && !isFormChanged(form)),
                    loading: form.isSubmitting,
                    children: resource ? 'Save' : 'Create'
                }}
            />
        </Box>
    );
};

export default memo(WaMessageSetupForm);
